import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { DropdownData, FilterRule } from './dropdown-data.interface';
import { SelectType } from './dropdown-type.enum';
import { ValidatorMessages } from '../form-input/validatorMessagesInputs';
import { ErrorMessageService } from '../form-input/error-message.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NgSelectComponent } from '@ng-select/ng-select';

@Component({
	selector: 'app-dropdown-search-select',
	templateUrl: './dropdown-search-select.component.html',
	styleUrls: ['./dropdown-search-select.component.scss']
})
export class DropdownSearchSelectComponent implements OnInit, OnChanges, OnDestroy {
	@Input() public dropdownData: DropdownData[];
	@Input() public selectedValue: unknown;
	@Input() public dropdownPlaceholder: string;
	@Input() public noDataFoundLabel: string;
	@Input() public dropdownFormControl: FormControl | AbstractControl;
	@Input() public compareFunction: any;
	@Input() public validatorMessages: ValidatorMessages[];
	@Input() public dropdownSelect: SelectType = SelectType.Simple;
	@Input() public bindProperty = 'value';
	@Input() public labelProperty = 'displayName';
	@Input() public hideClearButton = true;
	@Input() public enableAllActions = true;
	@Input() public searchEnable: true;
	@Input() public filterRules: FilterRule[] = null;
	@Input() public readOnly: boolean;
	@Input() public toolTip: boolean;
	@Input() public rightCheckbox: boolean;
	@Input() public fromOptimise: boolean;
	@Output() public selectionChange = new EventEmitter();
	@Output() public onClose = new EventEmitter();
	@Output() public filters = new EventEmitter();

	public dropdownDataBuffer: DropdownData[];
	public bufferSize = 500;
	public numberOfItemsFromEndBeforeFetchingMore = 10;
	public loading = false;
	public tooltipMessage = '';
	public dropdownSelectType = SelectType;
	public errorMessage: string;
	public isDestinationFocused: boolean;
	public independentFiler: boolean;

	public selectAll: boolean;
	public clearAll: boolean;

	private unsubscriber$: Subject<void> = new Subject<void>();

	private selectedFilters: any[] = [];

	constructor(public errorMessageService: ErrorMessageService) {}

	public ngOnInit(): void {
		this.errorMessage = this.errorMessageService.setErrorMessage(this.validatorMessages, this.dropdownFormControl);
		const controlValue = this.dropdownFormControl.value !== 'undefined' && this.dropdownFormControl.value ? this.dropdownFormControl.value : null;
		if (controlValue || this.selectedValue) {
			this.dropdownFormControl.setValue(controlValue ?? this.selectedValue);
		}
		this.dropdownFormControl.valueChanges.pipe(takeUntil(this.unsubscriber$)).subscribe(values => {
			this.selectAll = values?.length === this.dropdownData?.length && this.dropdownData?.length > 0;
			this.clearAll = this.dropdownData?.length > 0 && !values?.length;
		});

		if (this.filterRules) {
			this.filterRules.forEach(filterRule => {
				if (filterRule.selected) {
					this.selectedFilters.push(filterRule.key);
				}
			});

			if (!this.selectedFilters?.length) {
				this.initFilters();
			}
			this.bufferSize = 50;
		}
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (this.dropdownData) {
			if (this.dropdownFormControl.value?.length) {
				this.bufferSize = this.dropdownFormControl.value.length;
			}
			this.dropdownDataBuffer = this.dropdownData.slice(0, this.bufferSize);
		} else {
			this.dropdownDataBuffer = [];
		}

		if (this.dropdownFormControl.value) {
			this.fillBuffer();
		}
	}

	public ngOnDestroy(): void {
		this.unsubscriber$.next();
		this.unsubscriber$.complete();
	}

	public onSelectAll(): void {
		if (this.dropdownData?.length) {
			this.dropdownFormControl.setValue(this.dropdownData.map(data => data.value));
		}
		this.fillBuffer();
		this.goToTop();
		this.selectionChange.emit();
	}

	public onClearAll(): void {
		this.goToTop();
		this.resetBuffer();
		this.dropdownFormControl.setValue([]);
		this.selectionChange.emit();
	}

	public autocompleteFocus(): void {
		this.isDestinationFocused = true;
	}
	public changeText(): void {
		if (this.dropdownFormControl.value.length) {
			this.tooltipMessage = this.dropdownFormControl.value.join(' ; ');
		} else {
			this.tooltipMessage = '';
		}
	}
	public setErrorMessage(): void {
		this.errorMessage = this.errorMessageService.setErrorMessage(this.validatorMessages, this.dropdownFormControl);
	}

	public onChange(): void {
		this.selectionChange.emit();
	}

	public onDropdownClose(): void {
		this.onClose.emit();
	}

	public onScrollToEnd(): void {
		this.increaseBuffer();
	}

	public onScroll(index: { start: number; end: number }): void {
		if (this.loading || this.dropdownData.length <= this.dropdownDataBuffer.length) {
			return;
		}

		if (index.end + this.numberOfItemsFromEndBeforeFetchingMore >= this.dropdownData.length) {
			this.increaseBuffer();
		}
	}

	public onResize($event: any, select: NgSelectComponent): void {
		select.close();
	}

	public filterStatus(key: any, removeOthersFilters: boolean): void {
		if (!this.dropdownData) {
			this.dropdownDataBuffer = [];
		}
		if (this.selectedFilters.includes(key)) {
			this.selectedFilters = this.selectedFilters.filter(filterKey => filterKey !== key);
		} else {
			if (removeOthersFilters) {
				this.independentFiler = true;
				this.selectedFilters = [key];
			} else {
				if (this.independentFiler) {
					this.selectedFilters = [key];
					this.independentFiler = false;
				} else {
					this.selectedFilters.push(key);
				}
			}
		}

		this.resetSelectedFilters();

		if (!this.selectedFilters?.length) {
			this.initFilters();
		}

		this.filters.emit(this.selectedFilters);
	}

	private resetBuffer(): void {
		this.bufferSize = 50;
		if (this.dropdownData) {
			this.dropdownDataBuffer = this.dropdownData.slice(0, this.bufferSize);
		}
	}

	private fillBuffer(fill = false): void {
		if ((fill || this.dropdownFormControl.value?.length) && this.dropdownData) {
			this.bufferSize = this.dropdownData.length;
			this.dropdownDataBuffer = this.dropdownData.slice(0, this.bufferSize);
		}
	}

	private increaseBuffer(): void {
		const len = this.dropdownDataBuffer.length;
		const more = this.dropdownData.slice(len, this.bufferSize + len);

		this.loading = true;
		// simulate backend API delay
		setTimeout(() => {
			this.loading = false;
			this.dropdownDataBuffer = this.dropdownDataBuffer.concat(more);
		}, 50);
	}

	private goToTop(): void {
		setTimeout(() => {
			const scrollContainer = document.querySelector('.ng-dropdown-panel-items');
			if (scrollContainer) {
				scrollContainer.scrollTop = 0;
			}
		}, 0);
	}

	private initFilters(): void {
		this.filterRules.forEach((filter: FilterRule) => {
			if (filter.default) {
				this.selectedFilters = [filter.key];
				filter.selected = true;
			} else {
				filter.selected = false;
			}
		});
	}

	private resetSelectedFilters(): void {
		this.filterRules.forEach((filter: FilterRule) => {
			filter.selected = this.selectedFilters.includes(filter.key);
		});
	}

	public onSearch(searchItem: { term: string; items: any[] }): void {
		this.fillBuffer(true);
		if (!searchItem.term) {
			this.resetBuffer();
		}
	}
}
